import React, { useEffect, useRef, useState } from 'react';
import { Sun, Moon, Globe, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../../assets/ruraylogotwo.png';
import { useTranslation } from 'react-i18next';

interface NavbarProps {
  theme?: 'dark' | 'light' | 'galactic';
  onToggleTheme?: () => void;
  isHidden?: boolean;
  currentPage: string;
  onPageChange: (page: string, section?: string) => void;
}

const NAV_LINKS = [
  { id: 'producto', page: 'servicios', match: 'services', labelKey: 'navbar.products', fallback: 'Producto' },
  // Ancla a la sección #pricing del home, no una ruta propia — por eso 'match'
  // nunca coincide con currentPage (nunca se marca "activo").
  { id: 'precios', page: 'home', section: 'pricing', match: '__pricing__', labelKey: 'navbar.pricing', fallback: 'Precios' },
  { id: 'casos-uso', page: 'casos-uso', match: 'usecases', labelKey: 'navbar.usecases', fallback: 'Casos de Uso' },
  { id: 'nosotros', page: 'nosotros', match: 'about', labelKey: 'navbar.about', fallback: 'Nosotros' },
  { id: 'blog', page: 'blog', match: 'blog', labelKey: '', fallback: 'Blog' },
] as const;

const Navbar: React.FC<NavbarProps> = ({ theme, onToggleTheme, isHidden, currentPage, onPageChange }) => {
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Indicador deslizante detrás del link con foco/hover — mismo recurso que
  // usa la navbar de Tesla.com: una píldora que se desliza de un item a otro
  // en vez de solo cambiar de color.
  //
  // Se probó primero con `layoutId` de Framer Motion (una píldora que se
  // monta/desmonta en cada <li>, dejando que FM anime el FLIP automático).
  // Se descartó: en pruebas reales quedaba con una transformación a medio
  // resolver (matrix con scale/translate atascados) — un glitch conocido de
  // layoutId cuando el elemento se desmonta de un lado y se monta del otro
  // sin AnimatePresence de por medio para que FM alcance a medir la salida.
  // En su lugar: una sola píldora SIEMPRE montada, cuya posición/ancho se
  // miden con getBoundingClientRect y se animan a mano — sin montar/desmontar
  // nada, no hay forma de que quede a medio animar.
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const navListRef = useRef<HTMLUListElement>(null);
  const linkRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const [pillStyle, setPillStyle] = useState({ x: 0, width: 0, opacity: 0 });
  const { t, i18n } = useTranslation();

  const updatePill = (targetId: string | null) => {
    const container = navListRef.current;
    const li = targetId ? linkRefs.current[targetId] : null;
    if (!container || !li) {
      setPillStyle((s) => ({ ...s, opacity: 0 }));
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const liRect = li.getBoundingClientRect();
    setPillStyle({ x: liRect.left - containerRect.left, width: liRect.width, opacity: 1 });
  };

  const activeLinkId = NAV_LINKS.find((l) => currentPage === l.match)?.id ?? null;

  useEffect(() => {
    updatePill(hoveredLink ?? activeLinkId);
    // Los links son texto corto y su ancho no depende del viewport, pero el
    // logo/acciones sí reflowean el layout general — recalcular en resize
    // evita que la píldora quede desalineada tras cambiar el tamaño.
    const onResize = () => updatePill(hoveredLink ?? activeLinkId);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [hoveredLink, activeLinkId, currentPage]);

  const languageOptions = [
    { code: 'es', label: 'Español' },
    { code: 'en', label: 'English' },
    { code: 'pt', label: 'Português' }
  ];

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    setShowLangMenu(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Bloquea el scroll de fondo mientras el menú móvil está abierto, y permite
  // cerrarlo con Escape además del clic en el overlay / en un link.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileMenuOpen]);

  const goTo = (page: string, section?: string) => {
    onPageChange(page, section);
    setMobileMenuOpen(false);
  };

  return (
    <nav
      style={{
        // 'none' (no solo translateY(0)) cuando está visible: cualquier transform ≠ none
        // crea un containing block nuevo para descendientes position:fixed, y el overlay
        // + panel del menú móvil son fixed — con translateY(0) quedaban anclados a la
        // cajita del propio <nav> (~40px) en vez del viewport completo.
        transform: isHidden ? 'translateY(-100%)' : 'none',
        transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), background 0.5s var(--ease-out-expo), padding 0.5s var(--ease-out-expo)',
        opacity: isHidden ? 0 : 1,
        pointerEvents: isHidden ? 'none' : 'auto'
      }}
    >
      <div className={`container nav-inner${scrolled ? ' is-scrolled' : ''}`}>
        <a
          href="#"
          className="nav-logo"
          title={t('navbar.home_tooltip', 'Ir al inicio')}
          aria-label={t('navbar.home_tooltip', 'Ir al inicio')}
          onClick={(e) => {
            e.preventDefault();
            onPageChange('home');
          }}
        >
          <img
            src={logo}
            alt="Ruray Digital"
            className="nav-logo-img"
            style={{ filter: theme === 'dark' ? 'grayscale(100%) brightness(10) drop-shadow(0 0 10px rgba(255,255,255,0.1))' : 'grayscale(100%) brightness(0) drop-shadow(0 0 10px rgba(0,0,0,0.1))' }}
          />
        </a>

        <ul className="nav-links" ref={navListRef} onMouseLeave={() => setHoveredLink(null)}>
          <motion.span
            aria-hidden
            className="nav-pill"
            initial={{ x: 0, width: 0, opacity: 0 }}
            animate={{ x: pillStyle.x, width: pillStyle.width, opacity: pillStyle.opacity }}
            transition={{ type: 'spring', stiffness: 420, damping: 34 }}
          />
          {NAV_LINKS.map((link) => {
            const isActive = currentPage === link.match;
            return (
              <li
                key={link.id}
                ref={(el) => { linkRefs.current[link.id] = el; }}
                onMouseEnter={() => setHoveredLink(link.id)}
              >
                <a
                  href={'section' in link ? `/#${link.section}` : `/${link.page}`}
                  className={isActive ? 'active' : undefined}
                  onClick={(e) => {
                    e.preventDefault();
                    goTo(link.page, 'section' in link ? link.section : undefined);
                  }}
                >
                  {link.labelKey ? t(link.labelKey, link.fallback) : link.fallback}
                </a>
              </li>
            );
          })}
        </ul>

        <div className="nav-actions" style={{ gap: '1rem', display: 'flex', alignItems: 'center' }}>
          <div style={{ position: 'relative' }} className="nav-lang-wrap">
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              style={{
                background: showLangMenu ? 'rgba(255,255,255,0.1)' : 'transparent',
                border: 'none',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '5px',
                borderRadius: '50%',
                transition: 'background 0.3s',
                gap: '6px',
                fontFamily: 'var(--font-body)',
                fontSize: '0.85rem',
                fontWeight: 500
              }}
              title={t('navbar.language')}
              aria-label={t('navbar.language')}
              aria-haspopup="true"
              aria-expanded={showLangMenu}
              className="theme-toggle-btn"
            >
              <Globe size={18} strokeWidth={1.5} />
              {(i18n.language.split('-')[0] || 'es').toUpperCase()}
            </button>

            <AnimatePresence>
              {showLangMenu && (
                <>
                  <div
                    style={{ position: 'fixed', inset: 0, zIndex: 90 }}
                    onClick={() => setShowLangMenu(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '0.5rem',
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-light)',
                      borderRadius: '12px',
                      padding: '0.5rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      minWidth: '140px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                      zIndex: 100,
                      maxHeight: '350px',
                      overflowY: 'auto'
                    }}
                    className="lang-dropdown"
                  >
                    {languageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        onMouseEnter={(e) => {
                          if (!i18n.language.startsWith(lang.code)) {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!i18n.language.startsWith(lang.code)) {
                            e.currentTarget.style.background = 'transparent';
                          }
                        }}
                        style={{
                          background: i18n.language.startsWith(lang.code) ? 'var(--accent-teal)' : 'transparent',
                          color: i18n.language.startsWith(lang.code) ? '#000' : 'var(--text-primary)',
                          border: 'none',
                          padding: '10px 14px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontFamily: 'var(--font-body)',
                          fontSize: '0.9rem',
                          fontWeight: i18n.language.startsWith(lang.code) ? 'bold' : 'normal',
                          transition: 'background 0.2s',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        {lang.label}
                        <span style={{
                          fontSize: '0.7rem',
                          opacity: i18n.language.startsWith(lang.code) ? 0.9 : 0.5,
                          fontWeight: 'bold'
                        }}>
                          {lang.code.toUpperCase()}
                        </span>
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={onToggleTheme}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '5px',
              borderRadius: '50%',
              transition: 'background 0.3s'
            }}
            title={theme === 'dark' ? 'Modo Claro' : theme === 'light' ? 'Modo Galáctico' : 'Modo Oscuro'}
            aria-label={theme === 'dark' ? 'Modo Claro' : theme === 'light' ? 'Modo Galáctico' : 'Modo Oscuro'}
            className="theme-toggle-btn"
          >
            {theme === 'dark' ? <Moon size={20} strokeWidth={1.5} /> : theme === 'light' ? <Sun size={20} strokeWidth={1.5} /> : <Sparkles size={20} strokeWidth={1.5} />}
          </button>

          <motion.button
            onClick={() => onPageChange('home', 'cta-final')}
            className="btn-nav-premium"
            style={{ cursor: 'pointer' }}
            whileHover={{ scale: 1.035 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 500, damping: 28 }}
          >
            {t('navbar.sales')}
          </motion.button>

          <button
            onClick={() => setMobileMenuOpen((v) => !v)}
            className={`nav-hamburger${mobileMenuOpen ? ' is-open' : ''}`}
            aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          >
            <span className="nav-hamburger-line" />
            <span className="nav-hamburger-line" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="mobile-menu-overlay"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="mobile-menu-panel"
              onClick={() => setMobileMenuOpen(false)}
            >
              {/* El panel cubre toda la pantalla (por encima incluso de la navbar), así
                  que el botón hamburguesa que abrió el menú queda tapado — necesita su
                  propio botón de cierre visible aquí adentro. */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMobileMenuOpen(false);
                }}
                className="nav-hamburger is-open mobile-menu-close"
                aria-label="Cerrar menú"
              >
                <span className="nav-hamburger-line" />
                <span className="nav-hamburger-line" />
              </button>

              {/* Cada link entra con su propio delay — un solo reveal escalonado
                  se siente más cuidado que todo el panel apareciendo de golpe. */}
              {NAV_LINKS.map((link, i) => (
                <motion.a
                  key={link.id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.05 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  href={'section' in link ? `/#${link.section}` : `/${link.page}`}
                  className={`mobile-menu-link${currentPage === link.match ? ' active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    goTo(link.page, 'section' in link ? link.section : undefined);
                  }}
                >
                  {link.labelKey ? t(link.labelKey, link.fallback) : link.fallback}
                </motion.a>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
